import { Token, ChainId, Tokens, LidoTokenList } from '../constants';

import { handleTransactionError, isEthereum } from '@/utils';
import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import { useEffect, useMemo, useState } from 'react';
import { useGetContractByUserWeb3 } from './use-contract';
import ERC20_ABI from '@/contracts/erc20.json';
import {
    getBridgeSpenderByChainId,
    getLidoBridgeSpenderByChainId,
} from '@/contracts/utils';
import user from '@/stores/user';
import { Config as MetisAdapterConfig } from '@/contracts/metisLayzeroAdapter';

function getBridgeSpenderAddress(
    token: Token,
    fromChainId: ChainId,
    toChainId: ChainId,
) {
    if (LidoTokenList.includes(token)) {
        return getLidoBridgeSpenderByChainId(fromChainId).address;
    } else {
        if (
            fromChainId === ChainId.metisMainnet &&
            toChainId === ChainId.goatedMainnet
        ) {
            return MetisAdapterConfig[fromChainId]?.address;
        }
        return getBridgeSpenderByChainId(fromChainId).address;
    }
}

export default function useTokenApprove(
    token: Token,
    tokenAmount: BigNumber,
    fromChainId: ChainId,
    toChainId: ChainId,
) {
    const getContract = useGetContractByUserWeb3();

    const isGasToken = useMemo(() => {
        return isEthereum(fromChainId) && token === Token.eth;
    }, [fromChainId, token]);

    const tokenDetail = useMemo(() => {
        return Tokens[token] && fromChainId
            ? (Tokens[token] as any)?.[fromChainId]
            : undefined;
    }, [token, fromChainId]);
    const [approved, setApproved] = useState(false);

    async function getIsNeedToApprove() {
        if (isGasToken) {
            setApproved(true);
            return true;
        }
        if (!tokenDetail) return;
        try {
            let tokenAllowance = new BigNumber(0);
            const spender = getBridgeSpenderAddress(
                token,
                fromChainId,
                toChainId,
            );

            if (!spender) return;
            const tokenContract = await getContract(
                tokenDetail.address || '',
                ERC20_ABI,
                false,
            );

            if (!tokenContract) {
                console.error('Failed to get token contract');
                return false;
            }

            const allowanceRes = await tokenContract.allowance(
                user.address,
                spender,
            );

            if (allowanceRes && tokenDetail) {
                tokenAllowance = new BigNumber(
                    allowanceRes.toString(),
                ).shiftedBy(-tokenDetail.decimals);
                console.log(
                    `Token: ${token}, Allowance: ${tokenAllowance.toFixed()} (without decimals)`,
                );
            }

            const isNeedToApprove = tokenAllowance.isLessThan(tokenAmount);

            setApproved(!isNeedToApprove);
        } catch (e) {
            // handleTransactionError(e);
            console.error(e);
            console.error('getIsNeedToApprove fail');
        }
        return false;
    }

    async function approveUserTokenForSpender() {
        if (isGasToken) return true;
        try {
            if (!fromChainId) return false;
            const spender = getBridgeSpenderAddress(
                token,
                fromChainId,
                toChainId,
            );

            if (!approved) {
                const tokenContract = await getContract(
                    tokenDetail.address || '',
                    ERC20_ABI,
                    true,
                );

                if (!tokenContract) {
                    console.error('Failed to get token contract');
                    return false;
                }

                const balanceRes = await tokenContract.balanceOf(user.address);

                const approveAmount = balanceRes
                    ? tokenAmount
                          .shiftedBy(tokenDetail.decimals)
                          .integerValue()
                          .toFixed()
                    : ethers.MaxUint256.toString();

                // Send the approval transaction
                const tx = await tokenContract.approve(spender, approveAmount);
                const receipt = await tx.wait();

                if (receipt && receipt.hash) {
                    await getIsNeedToApprove();
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        } catch (e) {
            handleTransactionError(e);
            console.error(e);
            console.error('approveUserTokenForSpender fail');
        }
        return false;
    }

    useEffect(() => {
        if (
            user &&
            user.address &&
            fromChainId &&
            tokenAmount.isGreaterThan(0) &&
            !tokenDetail?.oft
        ) {
            getIsNeedToApprove();
        }
    }, [fromChainId, toChainId, tokenAmount, tokenDetail, user]);

    return {
        approveUserTokenForSpender,
        // oft token don't need approval
        approved: approved || !!tokenDetail?.oft,
    };
}
